import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useNavigate, Navigate } from "react-router-dom";
import { Loader2, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";

export default function LoginPage() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  // Forgot password
  const [showForgot, setShowForgot] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotMessage, setForgotMessage] = useState("");
  const [forgotBusy, setForgotBusy] = useState(false);

  if (user && user.id) return <Navigate to="/talepler" replace />;

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      await login(email.trim().toLowerCase(), password);
      toast.success("Giriş başarılı");
      navigate("/talepler");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Giriş yapılamadı");
    } finally {
      setBusy(false);
    }
  };

  const submitForgot = async () => {
    if (!forgotEmail.trim()) { toast.error("E-posta girin"); return; }
    setForgotBusy(true);
    try {
      const { data } = await api.post("/auth/forgot-password", {
        email: forgotEmail.trim().toLowerCase(),
        message: forgotMessage,
      });
      toast.success(data.message || "Talebiniz alındı");
      setShowForgot(false);
      setForgotEmail("");
      setForgotMessage("");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Talep gönderilemedi");
    } finally {
      setForgotBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-white">
      {/* Brand panel */}
      <div className="hidden lg:flex relative overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1534094830444-3a1e21f7e3e7?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njd8MHwxfHNlYXJjaHwxfHxtaW5pbWFsJTIwYXJjaGl0ZWN0dXJlfGVufDB8fHx3aGl0ZXwxNzgwNTc1NjY4fDA&ixlib=rb-4.1.0&q=85"
          alt=""
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-tr from-slate-900/70 via-slate-900/30 to-transparent" />
        <div className="relative z-10 flex flex-col justify-between p-10 text-white w-full">
          <div className="font-display text-2xl font-semibold tracking-tight">
            <span className="text-emerald-400">●</span> SatınAlTrack
          </div>
          <div>
            <h1 className="font-display text-4xl xl:text-5xl font-semibold tracking-tight leading-tight">
              Satın alma taleplerinizi<br />WhatsApp'a göndermeden<br />önce <span className="text-emerald-400">kayıt altına alın.</span>
            </h1>
            <p className="mt-5 text-slate-200/80 max-w-md text-base">
              Çift sipariş yok, takip kolay, raporlar her an elinizin altında.
            </p>
          </div>
          <div className="text-xs text-slate-300/80 flex items-center gap-2">
            <ShieldCheck className="h-4 w-4" /> Yetkilendirilmiş kullanıcılar için iç sistem.
          </div>
        </div>
      </div>

      {/* Form panel */}
      <div className="flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-sm">
          <div className="mb-8">
            <div className="lg:hidden font-display text-2xl font-semibold mb-6">
              <span className="text-emerald-700">●</span> SatınAlTrack
            </div>
            <h2 className="font-display text-3xl font-semibold tracking-tight">Giriş Yap</h2>
            <p className="text-sm text-slate-500 mt-2">Hesabınıza giriş yaparak talep oluşturun.</p>
          </div>
          <form onSubmit={onSubmit} className="space-y-5">
            <div>
              <Label htmlFor="email" className="text-xs font-bold uppercase tracking-wider text-slate-600">E-posta</Label>
              <Input
                id="email"
                data-testid="login-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-1.5 h-12 text-base"
                placeholder="ornek@firma.com"
                autoComplete="email"
                required
              />
            </div>
            <div>
              <Label htmlFor="password" className="text-xs font-bold uppercase tracking-wider text-slate-600">Şifre</Label>
              <Input
                id="password"
                data-testid="login-password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-1.5 h-12 text-base"
                autoComplete="current-password"
                required
              />
            </div>
            {error && (
              <div data-testid="login-error" className="text-sm text-rose-600 bg-rose-50 border border-rose-200 rounded-md px-3 py-2">{error}</div>
            )}
            <Button
              data-testid="login-submit"
              type="submit"
              disabled={busy}
              className="w-full h-12 bg-slate-900 hover:bg-slate-800 text-white text-base font-medium"
            >
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Giriş Yap"}
            </Button>
            <div className="text-center">
              <button
                type="button"
                data-testid="forgot-password-link"
                onClick={() => { setForgotEmail(email); setShowForgot(true); }}
                className="text-sm text-slate-500 hover:text-slate-900 underline-offset-4 hover:underline"
              >
                Şifremi unuttum
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Forgot password dialog */}
      <Dialog open={showForgot} onOpenChange={setShowForgot}>
        <DialogContent data-testid="forgot-dialog" className="max-w-md">
          <DialogHeader>
            <DialogTitle>Şifremi Unuttum</DialogTitle>
            <DialogDescription>
              E-postanızı girip yöneticinize bildirim gönderin. Yöneticiniz size yeni bir şifre oluşturup paylaşacaktır.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider">E-posta</Label>
              <Input
                data-testid="forgot-email"
                type="email"
                value={forgotEmail}
                onChange={(e) => setForgotEmail(e.target.value)}
                className="mt-1.5 h-11"
                placeholder="ornek@firma.com"
                autoFocus
              />
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider">Not (opsiyonel)</Label>
              <Textarea
                data-testid="forgot-message"
                value={forgotMessage}
                onChange={(e) => setForgotMessage(e.target.value.slice(0, 500))}
                placeholder="Yöneticinize bir not bırakın..."
                className="mt-1.5 min-h-[80px]"
                maxLength={500}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowForgot(false)}>Vazgeç</Button>
            <Button
              data-testid="forgot-submit"
              onClick={submitForgot}
              disabled={forgotBusy}
              className="bg-slate-900 hover:bg-slate-800"
            >
              {forgotBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Talep Gönder"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
